import Web3 from 'web3';
import { EIP1559SendOptions, getSendOptions, sendContractMethod } from '../../utils/web3';
import BigNumber from 'bignumber.js';
import { RootStore } from '../RootStore';
import { ContractSendMethod, SendOptions } from 'web3-eth-contract';
import { WIBBTC, ERC20_ABI, MAX, IBBTC, WIBBTC_ABI } from '../../config/constants';
import { extendObservable } from 'mobx';
import { GasSpeed } from '../../config/enums/gas-speed.enum';
import { TokenBalance } from './UserStore';

export class ContractStore {
	private store!: RootStore;

	constructor(store: RootStore) {
		this.store = store;
		extendObservable(this, {});
	}

	/* Contract Interaction Methods */

	mint = async (mintAmount: TokenBalance, userBalance: BigNumber): Promise<void> => {
		const { queueNotification } = this.store.uiState;
		const { wallet } = this.store.onboard;
		const amount = mintAmount.balance;

		if (!wallet?.provider) {
			console.error('Provider not valid when attempting to mint.');
			return;
		}

		if (amount.isNaN() || amount.lte(0) || amount.gt(userBalance)) {
			queueNotification('Please enter a valid amount', 'error');
			return;
		}
		const allowance = await this.getAllowance(IBBTC, WIBBTC);
		if (amount.gt(allowance)) {
			await this.increaseAllowance(IBBTC, WIBBTC);
		}
		const web3 = new Web3(wallet.provider);
		const wrappedIbbtc = new web3.eth.Contract(WIBBTC_ABI, WIBBTC);
		const method = wrappedIbbtc.methods.mint(amount.toFixed(0, BigNumber.ROUND_DOWN));
		const options = await this.getMethodSendOptions(method);
		const infoMessage = `Transaction to mint ${mintAmount.displayBalance} submitted`;
		const successMessage = `${mintAmount.displayBalance} wibBTC Minted!`;
		const errorMessage = 'Error minting wibBTC';
		await sendContractMethod(this.store, method, options, infoMessage, successMessage, errorMessage);
	};

	redeem = async (burnAmount: TokenBalance, userBalance: BigNumber): Promise<void> => {
		const { queueNotification } = this.store.uiState;
		const {
			onboard: { wallet }
		} = this.store;

		let amount = burnAmount.balance;
		if (amount.gt(userBalance)) {
			amount = userBalance;
		}

		if (!wallet?.provider) {
			console.error('Provider not valid when attempting to mint.');
			return;
		}

		if (amount.isNaN() || amount.lte(0) || amount.gt(userBalance)) {
			queueNotification('Please enter a valid amount', 'error');
			return;
		}
		const allowance = await this.getAllowance(WIBBTC, WIBBTC);
		if (amount.gt(allowance)) {
			await this.increaseAllowance(WIBBTC, WIBBTC);
		}
		const web3 = new Web3(wallet.provider);
		const wrappedIbbtc = new web3.eth.Contract(WIBBTC_ABI, WIBBTC);
		const shares = await wrappedIbbtc.methods.balanceToShares(amount.toFixed(0, BigNumber.ROUND_DOWN)).call();
		const method = wrappedIbbtc.methods.burn(shares);
		const options = await this.getMethodSendOptions(method);
		const infoMessage = `Transaction to burn ${burnAmount.displayBalance} submitted`;
		const successMessage = `${burnAmount.displayBalance} wibBTC Burned!`;
		await sendContractMethod(this.store, method, options, infoMessage, successMessage);
	};

	increaseAllowance = async (tokenAddress: string, contract: string): Promise<void> => {
		const {
			onboard,
			uiState: { queueNotification }
		} = this.store;

		const web3 = new Web3(onboard.wallet?.provider);
		const underlyingContract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
		const symbol = tokenAddress === IBBTC ? 'ibBTC' : 'wibBTC';
		// provide infinite approval
		const method: ContractSendMethod = underlyingContract.methods.approve(contract, MAX);
		const options = await this.getMethodSendOptions(method);
		const infoMessage = 'Transaction submitted';
		const successMessage = `${symbol} allowance increased`;

		queueNotification(`Sign the transaction to allow Badger to spend your ${symbol}`, 'info');
		await sendContractMethod(this.store, method, options, infoMessage, successMessage);
	};

	getAllowance = async (contractAddress: string, spender: string): Promise<BigNumber> => {
		const { onboard } = this.store;

		if (!onboard.address) {
			throw Error('Disconnected while fetching allowance');
		}

		const web3 = new Web3(onboard.wallet?.provider);
		const underlyingContract = new web3.eth.Contract(ERC20_ABI, contractAddress);
		const allowance = await underlyingContract.methods.allowance(onboard.address, spender).call();

		return new BigNumber(allowance);
	};

	getMethodSendOptions = async (method: ContractSendMethod): Promise<SendOptions | EIP1559SendOptions> => {
		const {
			onboard,
			network: { gasPrices }
		} = this.store;
		if (!onboard.address) {
			throw Error('Sending tx without a connected account');
		}
		const price = gasPrices ? gasPrices[GasSpeed.Fast] : 0;
		return await getSendOptions(method, onboard.address, price);
	};
}
